import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { SEOHead } from '@/components/SEOHead';
import { Layout } from '@/components/layout/Layout';
import { PropertyCard } from '@/components/property/PropertyCard';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Building2, MapPin, Phone, Mail, Globe, Users, Star, ChevronRight, Shield, MessageCircle, Home, Map as MapIcon, Building, Calendar, CheckCircle2 } from 'lucide-react';

const SECTIONS = [
  { id: 'properties', label: 'Properties', icon: Home },
  { id: 'plots', label: 'Plots', icon: MapIcon },
  { id: 'projects', label: 'Projects', icon: Building },
  { id: 'team', label: 'Team', icon: Users },
  { id: 'about', label: 'About', icon: Building2 },
];

export default function AgencyDetail() {
  const { id } = useParams<{ id: string }>();

  const { data: agency, isLoading } = useQuery({
    queryKey: ['agency', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('agencies').select('*').eq('id', id!).single();
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  const { data: agents } = useQuery({
    queryKey: ['agency-agents', id],
    queryFn: async () => {
      const { data: agentProfiles } = await supabase.from('agent_profiles').select('*').eq('agency_id', id!);
      if (!agentProfiles || agentProfiles.length === 0) return [];
      const userIds = agentProfiles.map(a => a.user_id);
      const { data: profiles } = await supabase.from('profiles').select('*').in('user_id', userIds);
      const profileMap = Object.fromEntries((profiles || []).map(p => [p.user_id, p]));
      return agentProfiles.map(a => ({ ...a, profile: profileMap[a.user_id] || null }));
    },
    enabled: !!id,
  });

  const agentUserIds = agents?.map((a: any) => a.user_id) || [];

  const { data: saleProperties } = useQuery({
    queryKey: ['agency-sale-properties', agentUserIds],
    queryFn: async () => {
      const { data } = await supabase
        .from('properties')
        .select('*, property_images(*)')
        .in('user_id', agentUserIds)
        .eq('status', 'active')
        .eq('listing_type', 'sale')
        .order('created_at', { ascending: false })
        .limit(12);
      return data || [];
    },
    enabled: agentUserIds.length > 0,
  });

  const { data: rentProperties } = useQuery({
    queryKey: ['agency-rent-properties', agentUserIds],
    queryFn: async () => {
      const { data } = await supabase
        .from('properties')
        .select('*, property_images(*)')
        .in('user_id', agentUserIds)
        .eq('status', 'active')
        .eq('listing_type', 'rent')
        .order('created_at', { ascending: false })
        .limit(12);
      return data || [];
    },
    enabled: agentUserIds.length > 0,
  });

  const { data: plots } = useQuery({
    queryKey: ['agency-plots', agentUserIds],
    queryFn: async () => {
      const { data } = await supabase
        .from('plots')
        .select('*, plot_images(*)')
        .in('user_id', agentUserIds)
        .eq('status', 'active')
        .eq('approval_status', 'approved')
        .order('created_at', { ascending: false })
        .limit(12);
      return data || [];
    },
    enabled: agentUserIds.length > 0,
  });

  const { data: projects } = useQuery({
    queryKey: ['agency-projects', agentUserIds],
    queryFn: async () => {
      const { data: devs } = await supabase.from('developer_profiles').select('id').in('user_id', agentUserIds);
      if (!devs || devs.length === 0) return [];
      const devIds = devs.map(d => d.id);
      const { data } = await supabase
        .from('projects')
        .select('*, project_images(*)')
        .in('developer_id', devIds)
        .eq('approval_status', 'approved')
        .order('created_at', { ascending: false })
        .limit(12);
      return data || [];
    },
    enabled: agentUserIds.length > 0,
  });

  if (isLoading) {
    return (
      <Layout>
        <div className="container py-8 space-y-4">
          <Skeleton className="h-32 rounded-xl" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-64 rounded-xl" />
        </div>
      </Layout>
    );
  }

  if (!agency) {
    return (
      <Layout>
        <div className="container py-20 text-center">
          <Building2 className="h-14 w-14 mx-auto text-muted-foreground/50 mb-4" />
          <h1 className="text-2xl font-bold">Agency not found</h1>
        </div>
      </Layout>
    );
  }

  const description = typeof agency.description === 'string'
    ? agency.description
    : (agency.description as any)?.en || '';

  const staffCount = agents?.length || 0;
  const saleCount = saleProperties?.length || 0;
  const rentCount = rentProperties?.length || 0;
  const plotsCount = plots?.length || 0;
  const projectsCount = projects?.length || 0;
  const propertiesCount = saleCount + rentCount;
  const totalListings = propertiesCount + plotsCount + projectsCount;

  return (
    <Layout>
      <SEOHead
        title={`${agency.name} — Real Estate Agency in ${agency.city || 'Ethiopia'}`}
        description={`${agency.name} is a ${agency.is_verified ? 'verified ' : ''}real estate agency${agency.city ? ' in ' + agency.city : ''}. ${propertiesCount} properties, ${plotsCount} plots, ${projectsCount} projects, ${staffCount} agents.`}
        canonical={`/agencies/${id}`}
      />

      {/* Breadcrumb */}
      <div className="border-b bg-card">
        <div className="container py-3">
          <nav className="flex items-center gap-2 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <ChevronRight className="h-3 w-3" />
            <Link to="/agencies" className="hover:text-foreground transition-colors">Agencies</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-foreground font-medium">{agency.name}</span>
          </nav>
        </div>
      </div>

      {/* Header */}
      <div className="border-b bg-card">
        <div className="container py-6">
          <div className="flex items-start gap-4 sm:gap-5 min-w-0">
            <Avatar className="h-16 w-16 sm:h-20 sm:w-20 rounded-lg border shadow-sm shrink-0">
              <AvatarImage src={agency.logo_url || undefined} className="object-cover" />
              <AvatarFallback className="text-2xl font-bold rounded-lg bg-muted text-muted-foreground">
                {agency.name.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-foreground break-words min-w-0">{agency.name}</h1>
                {agency.is_verified && (
                  <Badge className="bg-emerald-500 text-white border-0 gap-1 text-xs">
                    <Shield className="h-3 w-3" /> Verified
                  </Badge>
                )}
              </div>
              {agency.city && (
                <p className="flex items-start gap-1.5 mt-1.5 text-sm text-muted-foreground min-w-0">
                  <MapPin className="h-4 w-4 shrink-0 mt-0.5" />
                  <span className="break-words min-w-0">{agency.city}{agency.address && ` · ${agency.address}`}</span>
                </p>
              )}
              <div className="flex items-center gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground flex-wrap min-w-0">
                {agency.phone && <span className="flex items-center gap-1 min-w-0"><Phone className="h-3 w-3 shrink-0" /> <span className="truncate">{agency.phone}</span></span>}
                {agency.email && <span className="flex items-center gap-1 min-w-0 max-w-full"><Mail className="h-3 w-3 shrink-0" /> <span className="truncate">{agency.email}</span></span>}
                {agency.website && <a href={agency.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-primary hover:underline min-w-0"><Globe className="h-3 w-3 shrink-0" /> Website</a>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats bar */}
      <div className="border-b bg-muted/40">
        <div className="container relative after:pointer-events-none after:absolute after:inset-y-0 after:right-0 after:w-8 after:bg-gradient-to-l after:from-muted/40 after:to-transparent">
          <div className="flex items-center divide-x overflow-x-auto no-scrollbar">
            <StatItem label="Properties" value={propertiesCount} />
            <StatItem label="Plots" value={plotsCount} />
            <StatItem label="Projects" value={projectsCount} />
            <StatItem label="Staff" value={staffCount} />
          </div>
        </div>
      </div>

      {/* Sticky sub-nav */}
      <div className="sticky top-[96px] z-30 border-b bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80">
        <div className="container relative after:pointer-events-none after:absolute after:inset-y-0 after:right-0 after:w-8 after:bg-gradient-to-l after:from-card after:to-transparent">
          <nav className="flex items-center gap-1 overflow-x-auto no-scrollbar">
            {SECTIONS.map(s => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="flex items-center gap-1.5 px-4 py-3 text-sm font-medium text-muted-foreground hover:text-foreground border-b-2 border-transparent hover:border-primary/50 transition-colors whitespace-nowrap"
              >
                <s.icon className="h-4 w-4" /> {s.label}
              </a>
            ))}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="container py-6">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left — stacked sections */}
          <div className="lg:col-span-2 space-y-10">
            {/* Properties */}
            <section id="properties" className="scroll-mt-[160px]">
              <SectionHeader title="Properties" count={propertiesCount} />
              <PropertiesSection sale={saleProperties || []} rent={rentProperties || []} />
            </section>

            {/* Plots */}
            {plotsCount > 0 && (
              <section id="plots" className="scroll-mt-[160px]">
                <SectionHeader title="Plots & Land" count={plotsCount} />
                <div className="grid sm:grid-cols-2 gap-4">
                  {plots!.map((p: any) => <PlotCard key={p.id} plot={p} />)}
                </div>
              </section>
            )}

            {/* Projects */}
            {projectsCount > 0 && (
              <section id="projects" className="scroll-mt-[160px]">
                <SectionHeader title="New Projects" count={projectsCount} />
                <div className="grid sm:grid-cols-2 gap-4">
                  {projects!.map((p: any) => <ProjectCard key={p.id} project={p} />)}
                </div>
              </section>
            )}

            {/* Team */}
            <section id="team" className="scroll-mt-[160px]">
              <SectionHeader title="Our Team" count={staffCount} />
              {agents && agents.length > 0 ? (
                <div className="space-y-3">
                  {agents.map((agent: any) => (
                    <AgentRow key={agent.id} agent={agent} agencyName={agency.name} />
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm py-6 text-center bg-muted/30 rounded-lg">No staff members listed yet.</p>
              )}
            </section>

            {/* About */}
            <section id="about" className="scroll-mt-[160px]">
              <SectionHeader title={`About ${agency.name}`} />
              <Card className="rounded-xl">
                <CardContent className="p-5 space-y-4">
                  {description ? (
                    <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">No description provided.</p>
                  )}
                  <div className="grid sm:grid-cols-2 gap-4 pt-3 border-t text-sm">
                    {agency.city && <InfoRow icon={MapPin} label="Location" value={`${agency.city}${agency.address ? ', ' + agency.address : ''}`} />}
                    {agency.phone && <InfoRow icon={Phone} label="Phone" value={agency.phone} />}
                    {agency.email && <InfoRow icon={Mail} label="Email" value={agency.email} />}
                    {agency.website && <InfoRow icon={Globe} label="Website" value={agency.website} link={agency.website} />}
                    {agency.is_verified && <InfoRow icon={CheckCircle2} label="Status" value="Verified Agency" />}
                  </div>
                </CardContent>
              </Card>
            </section>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            <Card className="rounded-xl sticky top-[170px]">
              <CardContent className="p-5 space-y-3">
                {agency.phone && (
                  <Button asChild className="w-full bg-emerald-600 hover:bg-emerald-700 text-white gap-2 h-11 min-w-0">
                    <a href={`tel:${agency.phone}`}><Phone className="h-4 w-4 shrink-0" /> <span className="truncate">Call Agency</span></a>
                  </Button>
                )}
                {agency.email && (
                  <Button asChild variant="outline" className="w-full gap-2 h-11 min-w-0">
                    <a href={`mailto:${agency.email}`}><Mail className="h-4 w-4 shrink-0" /> <span className="truncate">Email</span></a>
                  </Button>
                )}
                {agency.phone && (
                  <Button asChild variant="outline" className="w-full gap-2 h-11 border-emerald-600 text-emerald-600 hover:bg-emerald-50 min-w-0">
                    <a href={`https://wa.me/${agency.phone.replace(/\D/g, '')}`} target="_blank" rel="noreferrer">
                      <MessageCircle className="h-4 w-4 shrink-0" /> <span className="truncate">WhatsApp</span>
                    </a>
                  </Button>
                )}
                {/* Quick stats */}
                <div className="pt-4 border-t">
                  <h3 className="text-xs font-semibold uppercase text-muted-foreground mb-2.5">Quick Stats</h3>
                  <div className="space-y-2 text-sm">
                    <StatRow label="Total Listings" value={totalListings} />
                    <StatRow label="Active Agents" value={staffCount} />
                    <StatRow label="Verified" value={agency.is_verified ? 'Yes ✓' : 'No'} />
                  </div>
                </div>

                {/* Office location */}
                {(agency.city || agency.address) && (
                  <div className="pt-4 border-t">
                    <h3 className="text-xs font-semibold uppercase text-muted-foreground mb-2.5">Office Location</h3>
                    <div className="flex items-start gap-2 text-sm text-muted-foreground">
                      <MapPin className="h-4 w-4 shrink-0 mt-0.5 text-primary" />
                      <span>{agency.address || agency.city}{agency.address && agency.city ? `, ${agency.city}` : ''}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}

/* ===== Helpers & inline components ===== */

function StatItem({ label, value }: { label: string; value: number }) {
  return (
    <div className="py-3 px-6 first:pl-0 first:pr-6 whitespace-nowrap">
      <span className="text-lg font-bold text-foreground">{value}</span>
      <span className="text-sm text-muted-foreground ml-1.5">{label}</span>
    </div>
  );
}

function SectionHeader({ title, count }: { title: string; count?: number }) {
  return (
    <div className="flex items-baseline justify-between mb-4">
      <h2 className="text-lg md:text-xl font-bold text-foreground">
        {title}
        {count !== undefined && <span className="text-muted-foreground font-normal ml-2">({count})</span>}
      </h2>
    </div>
  );
}

function StatRow({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold text-foreground">{value}</span>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, link }: { icon: any; label: string; value: string; link?: string }) {
  return (
    <div className="flex items-start gap-2.5">
      <Icon className="h-4 w-4 mt-0.5 text-primary shrink-0" />
      <div className="min-w-0">
        <div className="text-xs text-muted-foreground">{label}</div>
        {link ? (
          <a href={link} target="_blank" rel="noreferrer" className="text-foreground font-medium hover:text-primary truncate block">{value}</a>
        ) : (
          <div className="text-foreground font-medium truncate">{value}</div>
        )}
      </div>
    </div>
  );
}

function PropertiesSection({ sale, rent }: { sale: any[]; rent: any[] }) {
  const [tab, setTab] = useState<'sale' | 'rent'>(sale.length >= rent.length ? 'sale' : 'rent');
  const list = tab === 'sale' ? sale : rent;

  if (sale.length === 0 && rent.length === 0) {
    return <p className="text-muted-foreground text-sm py-6 text-center bg-muted/30 rounded-lg">No properties listed yet.</p>;
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => setTab('sale')}
          className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
            tab === 'sale' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/70'
          }`}
        >
          For Sale ({sale.length})
        </button>
        <button
          onClick={() => setTab('rent')}
          className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
            tab === 'rent' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/70'
          }`}
        >
          For Rent ({rent.length})
        </button>
      </div>
      {list.length > 0 ? (
        <div className="grid sm:grid-cols-2 gap-4">
          {list.map((p: any) => <PropertyCard key={p.id} property={p} />)}
        </div>
      ) : (
        <p className="text-muted-foreground text-sm py-6 text-center bg-muted/30 rounded-lg">No {tab === 'sale' ? 'sale' : 'rent'} listings.</p>
      )}
    </div>
  );
}

function PlotCard({ plot }: { plot: any }) {
  const img = plot.plot_images?.find((i: any) => i.is_primary)?.url || plot.plot_images?.[0]?.url;
  const title = typeof plot.title === 'string' ? plot.title : plot.title?.en || 'Plot';
  return (
    <Link to={`/plot/${plot.id}`} className="group block">
      <Card className="rounded-xl overflow-hidden hover:shadow-md transition-shadow">
        <div className="aspect-[4/3] bg-muted overflow-hidden">
          {img ? (
            <img src={img} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center"><MapIcon className="h-10 w-10 text-muted-foreground/40" /></div>
          )}
        </div>
        <CardContent className="p-3 space-y-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className="font-bold text-primary text-sm">
              {plot.currency || 'ETB'} {Number(plot.price).toLocaleString()}
            </span>
            <Badge variant="secondary" className="text-[10px] capitalize">{plot.listing_type}</Badge>
          </div>
          <h3 className="text-sm font-medium line-clamp-1">{title}</h3>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {plot.city}</span>
            {plot.area_sqm && <span>{plot.area_sqm} m²</span>}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function ProjectCard({ project }: { project: any }) {
  const img = project.project_images?.find((i: any) => i.is_primary)?.url || project.project_images?.[0]?.url;
  const name = typeof project.name === 'string' ? project.name : project.name?.en || 'Project';
  return (
    <Link to={`/projects/${project.id}`} className="group block">
      <Card className="rounded-xl overflow-hidden hover:shadow-md transition-shadow">
        <div className="aspect-[4/3] bg-muted overflow-hidden relative">
          {img ? (
            <img src={img} alt={name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center"><Building className="h-10 w-10 text-muted-foreground/40" /></div>
          )}
          {project.status && (
            <Badge className="absolute top-2 left-2 capitalize bg-primary text-primary-foreground border-0 text-[10px]">
              {String(project.status).replace('_', ' ')}
            </Badge>
          )}
        </div>
        <CardContent className="p-3 space-y-1">
          <h3 className="text-sm font-semibold line-clamp-1">{name}</h3>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <MapPin className="h-3 w-3" /> {project.city}
          </div>
          {(project.price_min || project.price_max) && (
            <div className="text-sm font-bold text-primary pt-1">
              {project.currency || 'ETB'} {Number(project.price_min || 0).toLocaleString()}
              {project.price_max && project.price_max !== project.price_min && ` – ${Number(project.price_max).toLocaleString()}`}
            </div>
          )}
          {project.completion_date && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground pt-0.5">
              <Calendar className="h-3 w-3" /> Completion: {new Date(project.completion_date).getFullYear()}
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}

function AgentRow({ agent, agencyName }: { agent: any; agencyName?: string }) {
  return (
    <div className="flex items-center gap-4 p-4 border rounded-xl bg-card hover:shadow-sm transition-shadow">
      <Avatar className="h-14 w-14 shrink-0">
        <AvatarImage src={agent.profile?.avatar_url} />
        <AvatarFallback className="text-sm font-bold">
          {(agent.profile?.full_name || '?').slice(0, 2).toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <h3 className="font-semibold text-sm">{agent.profile?.full_name || 'Agent'}</h3>
        {agencyName && (
          <p className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
            <Building2 className="h-3 w-3" /> {agencyName}
          </p>
        )}
        <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
          {agent.rating > 0 && (
            <span className="flex items-center gap-1">
              <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
              {Number(agent.rating).toFixed(1)}
            </span>
          )}
          {agent.experience_years > 0 && <span>{agent.experience_years} yrs exp</span>}
          {agent.total_listings > 0 && <span>{agent.total_listings} listings</span>}
        </div>
      </div>
    </div>
  );
}
